import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { WS_BASE_URL, STORAGE_KEYS } from "../containers/Pages/Chat/services/ApisConstants";

class StompChatService {
  constructor() {
    this.client = null;
    this.connected = false;
    this.currentChatId = null;
    this.messageCallback = null; // Store message callback
    this.orgId = null; // Store orgId for subscription
    this._userId = null; // Store userId for subscriptions
    this._authFailCount = 0; // Track consecutive auth failures
    // Group chat
    this._groupSubs = [];
    this._groupMessageHandler = null;
    this._lastGroupId = null;
    this._lastGroupUserId = null;
  }

  // Helper to get token from cookies
  _getTokenFromCookies() {
    const getCookie = (cookieName) => {
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith(`${encodeURIComponent(cookieName)}=`)) {
          return decodeURIComponent(trimmedCookie.substring(cookieName.length + 1));
        }
      }
      return null;
    };
    return getCookie(STORAGE_KEYS.refreshToken) || getCookie(STORAGE_KEYS.accessToken);
  }

  // Connect to WebSocket server
  connect(jwtToken, onConnected, onMessage, onError, orgId, userId) {
    // Don't connect without required credentials
    if (!userId || !orgId) {
      console.warn("StompChatService: Cannot connect without userId and orgId");
      return;
    }

    // Disconnect existing client before creating a new one (#9: prevent duplicate clients)
    if (this.client) {
      this.client.deactivate();
      this.client = null;
    }

    // Store orgId, userId, and message callback for later use
    this.orgId = orgId;
    this._userId = userId;
    this._jwtToken = jwtToken; // Store for reconnect fallback
    this.messageCallback = onMessage;
    this._authFailCount = 0;
    this._dead = false; // Track if connection was permanently killed

    const refreshToken = this._getTokenFromCookies() || jwtToken;

    // Don't connect without a token — would create anonymous session that can't subscribe
    if (!refreshToken) {
      console.warn("StompChatService: Cannot connect without auth token");
      return;
    }

    this.client = new Client({
      webSocketFactory: () => new SockJS(WS_BASE_URL),
      // #3: Use beforeConnect to refresh headers on every reconnect attempt
      beforeConnect: () => {
        const freshToken = this._getTokenFromCookies() || this._jwtToken;
        if (!freshToken || !this._userId) {
          // No valid credentials — deactivate after this callback returns
          this._dead = true;
          return;
        }
        this.client.connectHeaders = {
          Authorization: `Bearer ${freshToken}`,
          session_id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          Chat: true,
          connectionType: "customerService",
        };
      },
      connectHeaders: {
        Authorization: `Bearer ${refreshToken}`,
        session_id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        Chat: true,
        connectionType: "customerService",
      },
      reconnectDelay: 5000,
      onConnect: (frame) => {
        // #2/#7: If marked dead during beforeConnect, defer disconnect to avoid calling inside callback
        if (this._dead) {
          console.warn("StompChatService: No valid credentials, stopping connection");
          setTimeout(() => this.disconnect(), 0);
          return;
        }

        this.connected = true;
        this._authFailCount = 0;
        if (onConnected) onConnected(frame);

        // Clear stale subscriptions from previous connection (reconnect safety)
        this._chatInitSub = null;
        this._privateSub = null;

        // Subscribe to chat init
        this._chatInitSub = this.client.subscribe(`/topic/chat/${this.orgId}`, (msg) => {
          const payload = JSON.parse(msg.body);
          this.currentChatId = payload.chatId;
          localStorage.setItem("currentChatId", this.currentChatId);

          const normalizedMessage = this.normalizeWebSocketMessage(payload);
          if (onMessage) onMessage(normalizedMessage);
        });

        // Subscribe to private messages
        this._privateSub = this.client.subscribe(
          `/socket-user/${this._userId}/topic/private`,
          (msg) => {
            const payload = JSON.parse(msg.body);
            const normalizedMessage = this.normalizeWebSocketMessage(payload);
            if (onMessage) onMessage(normalizedMessage);
          }
        );

        // Re-subscribe to group channel after reconnect if one was active
        if (this._groupMessageHandler && this._lastGroupId && this._lastGroupUserId) {
          this._subscribeGroupInternal(
            this._groupMessageHandler,
            this._lastGroupUserId,
            this._lastGroupId
          );
        }
      },
      onStompError: (frame) => {
        const errorMsg = frame.headers?.message || "";
        console.error("STOMP broker error:", errorMsg);

        // #6: Use error code matching instead of fragile string matching
        const isAuthError = errorMsg.includes("CHK$SESS$") || errorMsg.includes("CHK%24SESS%24");
        if (isAuthError) {
          this._authFailCount += 1;
          if (this._authFailCount >= 3) {
            console.warn("StompChatService: Too many auth failures, stopping reconnect");
            this._dead = true;
            setTimeout(() => this.disconnect(), 0);
            return;
          }
        }

        if (onError) onError(frame);
      },
      onWebSocketClose: () => {
        this.connected = false;
      },
    });

    this.client.activate();
  }

  // Normalize WebSocket message to match API message format
  normalizeWebSocketMessage(wsMessage) {
    // Check if it's already normalized
    if (wsMessage.sender && wsMessage.sender.agent !== undefined) {
      return wsMessage;
    }

    // Handle messages without sender (like bot messages or system messages)
    const hasSender = wsMessage.sender || wsMessage.senderId || wsMessage.senderName;

    // Determine agent status first
    const isAgent = this.determineAgentStatus(wsMessage);

    // Normalize WebSocket message structure
    const normalized = {
      ...wsMessage,
      // Ensure sender structure matches API format
      sender: hasSender ? {
        ...wsMessage.sender,
        // Use the determined agent status
        agent: isAgent,
        // Add other required fields if missing
        _id: wsMessage.sender?._id || wsMessage.senderId,
        name: wsMessage.sender?.name || wsMessage.senderName || (isAgent ? "Agent" : "Customer"),
        roles: wsMessage.sender?.roles || [],
        isEmployee: wsMessage.sender?.isEmployee || isAgent
      } : {
        // Default sender for messages without sender info
        _id: null,
        name: "System",
        agent: false,
        roles: [],
        isEmployee: false
      },
      // Ensure message has proper structure
      _id: wsMessage._id || wsMessage.messageId || `ws_${Date.now()}`,
      messageId: wsMessage.messageId || wsMessage._id,
      chatId: wsMessage.chatId || this.currentChatId,
      content: wsMessage.content || "",
      createdAt: wsMessage.createdAt || new Date().toISOString(),
      // Add message type for proper handling
      type: wsMessage.type || "new_message"
    };

    return normalized;
  }

  // Determine if sender is agent based on WebSocket message data
  determineAgentStatus(wsMessage) {
    // Method 1: Check senderType (clear classification)
    // senderType: "USER" = Customer, "AGENT" = Agent, "bot" = Bot
    if (wsMessage.senderType === "AGENT" || wsMessage.senderType === "agent") {
      return true;
    }

    if (wsMessage.senderType === "USER" || wsMessage.senderType === "user") {
      return false;
    }

    if (wsMessage.senderType === "bot" || wsMessage.senderType === "BOT") {
      return false;
    }

    // Method 2: Check if sender is an employee/agent based on roles
    if (wsMessage.sender?.roles && Array.isArray(wsMessage.sender.roles)) {
      const hasOrganizationRole = wsMessage.sender.roles.some(role =>
        role.includes("ORGANIZATION") ||
        role.includes("EMPLOYEE") ||
        role.includes("MANAGER") ||
        role.includes("ADMIN")
      );
      if (hasOrganizationRole) {
        return true;
      }
    }

    // Method 3: Check isEmployee flag
    if (wsMessage.sender?.isEmployee === true) {
      return true;
    }

    // Method 4: Check senderId pattern (fallback) - IMPORTANT: This is the key method for WebSocket messages
    if (wsMessage.senderId && typeof wsMessage.senderId === "number" && wsMessage.senderId <= 100) {
      return true;
    }

    // Method 5: Check if it's a bot/system message (no sender info at all)
    // Only consider it a bot message if there's absolutely no sender information
    if (!wsMessage.sender && !wsMessage.senderId && !wsMessage.senderName && !wsMessage.senderType) {
      return false;
    }

    // Method 6: Check for specific error messages to filter out
    if (wsMessage.content?.toLowerCase().includes("sorry, i encountered an error")) {
      return false;
    }

    // Method 6: Check if senderId is between 1-100 (agents) vs 101+ (customers) - Extended range
    if (wsMessage.senderId && typeof wsMessage.senderId === "number") {
      if (wsMessage.senderId <= 100) {
        return true;
      }
      return false;
    }

    // Default to false (customer)
    return false;
  }

  // ---- Group chat subscriptions ----

  _subscribeGroupInternal(handler, userId, groupId) {
    // #5: Guard against missing userId/groupId to prevent subscribing to /socket-user/undefined/...
    if (!userId || !groupId || !this.client) return;

    const userSub = this.client.subscribe(
      `/socket-user/${userId}/topic/group`,
      (msg) => handler(JSON.parse(msg.body))
    );
    const groupSub = this.client.subscribe(
      `/topic/group/${groupId}`,
      (msg) => handler(JSON.parse(msg.body))
    );
    this._groupSubs = [userSub, groupSub];
  }

  subscribeGroupMessages(handler, userId, groupId) {
    this.unsubscribeGroupMessages();
    this._groupMessageHandler = handler;
    this._lastGroupId = groupId;
    this._lastGroupUserId = userId;
    if (this.connected && this.client) {
      this._subscribeGroupInternal(handler, userId, groupId);
    }
    // If not connected yet, will be subscribed on onConnect via stored refs
  }

  unsubscribeGroupMessages() {
    this._groupSubs.forEach((sub) => {
      try { sub.unsubscribe(); } catch (_) { /* ignore */ }
    });
    this._groupSubs = [];
    this._groupMessageHandler = null;
    this._lastGroupId = null;
    this._lastGroupUserId = null;
  }

  sendGroupMessage(token, { content, groupId, senderId }) {
    if (!this.client || !this.connected) return;
    this.client.publish({
      destination: "/app/social/message/group",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content, groupId, senderId }),
    });
  }

  sendGroupTyping(token, { senderId, groupId }) {
    if (!this.client || !this.connected) return;
    this.client.publish({
      destination: "/app/social/typing/group",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ senderId, groupId }),
    });
  }

  sendGroupStopTyping(token, { senderId, groupId }) {
    if (!this.client || !this.connected) return;
    this.client.publish({
      destination: "/app/social/stop-typing/group",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ senderId, groupId }),
    });
  }

  // Send message
  sendMessage(messageData, token) {
    if (!this.client || !this.connected) {
      console.error("Cannot send message: not connected");
      return;
    }

    if (!messageData?.chatId) {
      console.error("Cannot send message: no chatId provided");
      return;
    }

    const refreshToken = this._getTokenFromCookies() || token;

    const payload = {
      content: messageData?.content || "",
      chatId: messageData?.chatId,
      senderId: messageData?.senderId,
      attachments: messageData?.attachments
    };

    this.client.publish({
      destination: "/app/message",
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
      body: JSON.stringify(payload),
    });
  }

  // Disconnect
  disconnect() {
    if (this.client) {
      this.client.deactivate();
      this.client = null;
      this.connected = false;
      this._authFailCount = 0;
    }
  }
}

const stompChatService = new StompChatService();
export default stompChatService;
